import { useCallback, useEffect, useState, ChangeEvent } from "react";

export interface StoredFile {
  file: File;
  preview: string;
  id: string;
  extension?: string;
  error?: string;
  reason?: "type" | "size" | "load" | "length";
  isGenerated?: boolean;
}

export interface ProcessFilesConfig {
  onRemovedCallback?: (fileId: string) => void;
}

// 支持的格式定义
export const SUPPORTED_FORMATS = {
  png: "image/png",
  jpeg: "image/jpeg",
  webp: "image/webp",
  heic: "image/heic",
  heif: "image/heif",
} as const;

// 最大文件数和文件大小常量
export const MAX_IMAGES = 1;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 20MB

// Detect if browser supports a specific MIME type
const isImageTypeSupported = (path: string): Promise<boolean> => {
  return new Promise((resolve) => {
    if (!path) {
      resolve(false);
    }
    const img = new Image();
    const samplePath = path; // Fallback to HEIF if mimeType not found
    img.src = samplePath;
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
  });
};

export const useProcessFiles = ({
  onRemovedCallback,
}: ProcessFilesConfig = {}) => {
  const [selectedFiles, setSelectedFiles] = useState<
    Record<string, StoredFile>
  >({});

  const [supportedTypes, setSupportedTypes] = useState<string[]>([]);
  const [acceptString, setAcceptString] = useState<string>("");

  useEffect(() => {
    const checkSupportedTypes = async () => {
      const initialTypes = Object.values(SUPPORTED_FORMATS);
      const checkedTypes: string[] = [];

      for (const type of initialTypes) {
        if (type === SUPPORTED_FORMATS.heic) {
          const isSupported = await isImageTypeSupported("/images/sample.heic");
          if (isSupported) checkedTypes.push(type);
        } else if (type === SUPPORTED_FORMATS.heif) {
          const isSupported = await isImageTypeSupported(
            "/images/sample1.heif"
          );
          if (isSupported) checkedTypes.push(type);
        } else {
          checkedTypes.push(type);
        }
      }

      const accept = [
        ...Object.keys(SUPPORTED_FORMATS)
          .filter((ext) =>
            checkedTypes.includes(
              SUPPORTED_FORMATS[ext as keyof typeof SUPPORTED_FORMATS]
            )
          )
          .map((ext) => `.${ext}`),
        ...checkedTypes,
      ].join(",");

      setSupportedTypes(checkedTypes);
      setAcceptString(accept);
    };

    checkSupportedTypes();
  }, []);

  const getFileExtension = (file: File): string => {
    const type = file.type.split("/")[1] || file.name.split(".").pop() || "";
    return type.toUpperCase();
  };

  const processFiles = useCallback(
    (files: FileList, event?: ChangeEvent<HTMLInputElement>) => {
      if (!files || files.length === 0) return;

      const newFiles: Record<string, StoredFile> = {};
      let processedCount = 0;

      const updateStateIfComplete = () => {
        if (processedCount === files.length) {
          setSelectedFiles((prev) => {
            const allFiles = { ...prev, ...newFiles };
            const sortedFiles = Object.values(allFiles).sort(
              (a, b) =>
                parseInt(b.id.split("-")[0]) - parseInt(a.id.split("-")[0])
            );
            const potentialValidFiles = sortedFiles.filter(
              (file) => !file.error
            );
            const updatedFiles: Record<string, StoredFile> = {};

            // 标记超限文件
            const excessFiles = potentialValidFiles.slice(0, -MAX_IMAGES);
            excessFiles.forEach((file) => {
              updatedFiles[file.id] = {
                ...file,
                error: "File exceeds maximum limit",
                reason: "length",
              };
            });

            // 保留其他错误文件
            sortedFiles.forEach((file) => {
              if (!potentialValidFiles.includes(file)) {
                updatedFiles[file.id] = file;
              }
            });

            // 保留最后 MAX_IMAGES 个有效文件
            const validFiles = potentialValidFiles.slice(-MAX_IMAGES);
            validFiles.forEach((file) => {
              updatedFiles[file.id] = {
                ...file,
                error: undefined,
                reason: undefined,
              };
            });

            return updatedFiles;
          });

          
      if (event) {
        event.target.value = "";
      }
        }
      };

      Array.from(files).forEach((file) => {
        const fileType = file.type.toLowerCase();
        const fileId = `${Date.now()}-${Math.random()
          .toString(36)
          .substring(2)}`;
        const extension = getFileExtension(file);
        const reader = new FileReader();

        // 安全检查 fileType 是否在 supportedTypes 中
        if (!supportedTypes.includes(fileType)) {
          newFiles[fileId] = {
            file,
            preview: "",
            id: fileId,
            extension,
            error: "Unsupported file type",
            reason: "type",
          };
          processedCount += 1;
          updateStateIfComplete();
        } else if (file.size > MAX_FILE_SIZE) {
          newFiles[fileId] = {
            file,
            preview: "",
            id: fileId,
            extension,
            error: "File too large",
            reason: "size",
          };
          processedCount += 1;
          updateStateIfComplete();
        } else {
          reader.onload = (e) => {
            const previewUrl = e.target?.result as string;
            if (previewUrl) {
              newFiles[fileId] = {
                file,
                preview: previewUrl,
                id: fileId,
                extension,
              };
            }
            processedCount += 1;
            updateStateIfComplete();
          };
          reader.onerror = () => {
            newFiles[fileId] = {
              file,
              preview: "",
              id: fileId,
              extension,
              error: "File read failed",
              reason: "load",
            };
            processedCount += 1;
            updateStateIfComplete();
          };
          reader.readAsDataURL(file);
        }
      });
    },
    [supportedTypes]
  );

  // 移除指定的文件
  const removeFile = useCallback(
    (fileId: string) => {
      setSelectedFiles((prev) => {
        const newFiles = { ...prev };
        const isValidImage = newFiles[fileId] && !newFiles[fileId].error;

        delete newFiles[fileId];

        if (isValidImage) {
          const filesWithLengthError = Object.values(newFiles).filter(
            (file) => file.error && file.reason === "length"
          );
          if (filesWithLengthError.length > 0) {
            const lastErrorFile =
              filesWithLengthError[filesWithLengthError.length - 1];
            newFiles[lastErrorFile.id] = {
              ...lastErrorFile,
              error: undefined,
              reason: undefined,
            };
          }
        }

        return newFiles;
      });

      onRemovedCallback?.(fileId);
    },
    [onRemovedCallback]
  );

  // 移除所有已选择的文件
  const resetFiles = () => {
    setSelectedFiles({});
  };

  // 更新文件
  const updateFile = (updateFile: StoredFile) => {
    setSelectedFiles({ [updateFile.id]: { ...updateFile, isGenerated: true } });
  };

  return {
    selectedFiles,
    acceptString,
    setSelectedFiles,
    processFiles,
    removeFile,
    resetFiles,
    updateFile,
  };
};
